import { useMemo } from 'react';
import { TileGrid } from '../../tiles/TileGrid/TileGrid';
import { EntityTileProps } from '../../tiles/interfaces/tile';
import { EntityTile } from '../../tiles/EntityTile/EntityTile';
import { DestinationTilesFactory } from './factories/destinationTilesFactory';
import { SelectDestinationTypeCacheState } from './cache/interface';
import { DestinationType } from '../../shared/interfaces/destination';
import { DestinationAPI } from '../api/DestinationAPI';
import { DestinationTypesFactory } from './factories/destinationTypesFactory';
import { EntityList } from '../../entity-list/EntityList';
import { ListComponentBaseProps } from '../../entity-list/interface';
import { useInMemoryFilteredList } from '../../shared/hooks/useInMemoryFilteredList';
import { matchPattern } from '../../shared/utils/string/pattern';
import { EntityListEntities } from '../../shared/interfaces/entity-list';

export interface SelectDestinationTypeProps {
  onDestinationTypeSelect: (destinationType: DestinationType) => void;
  initialData?: Partial<SelectDestinationTypeCacheState>;
}

export function SelectDestinationType({
  onDestinationTypeSelect,
  initialData
}: SelectDestinationTypeProps) {
  const { fetch } = useInMemoryFilteredList(
    async () => {
      const res = await DestinationAPI.getDestinationTypes();
      return DestinationTypesFactory(res.data);
    },
    (item, params) => {
      return matchPattern(item.displayName, params.search as string);
    },
    initialData?.destinationTypes
  );

  return (
    <EntityList
      entity={EntityListEntities.DESTINATION_TYPE}
      mode='standalone'
      dataSource$={fetch}
      listIcon='destinations'
      emptyTitle='No Matching Destination Types Found'
      emptySubTitle='Modify your search filter term to get the desired result'
      showSearch={false}
      genericPageShimmer
      hidePagination
      hideRefresh
      newListConnectedTitle='No Destination Type Available'
      genericErrorTitle='Error while fetching Destination Types'
      genericErrorSubTitle='Retry Loading the Destination Types'
      inMemoryList
      ListComponent={ListComponent}
      listComponentProps={{
        onDestinationTypeSelect
      }}
    />
  );
}

function ListComponent({
  data,
  onDestinationTypeSelect
}: { onDestinationTypeSelect: (type: DestinationType) => void } & ListComponentBaseProps<
  DestinationType[]
>) {
  const destinationTiles = useMemo(() => {
    return DestinationTilesFactory(data || []);
  }, [data]);

  return (
    <TileGrid<DestinationType, EntityTileProps<DestinationType>>
      tiles={destinationTiles}
      onTileClick={tile => onDestinationTypeSelect(tile.data)}
      TileComponent={EntityTile}
      className='w-100'
    />
  );
}
